/**
 * Main hook for the chat view that orchestrates all chat-related hooks.
 * Manages conversation state, messages, input, split view, and WebSocket connection.
 */

import type { WebSocketConnectionStatus } from '@saaga/shared/hooks/use-agents-websocket.hook'

import type { useSplitView } from '@/hooks/use-split-view.hook'

import type {
  ChatViewHandlers,
  ChatViewState,
  ChatViewTitleEditState,
} from './chat-view-state.helper'
import {
  buildChatViewHandlers,
  buildChatViewState,
  buildTitleEditState,
} from './chat-view-state.helper'
import type { UseChatParticipantActionsReturn } from './use-chat-participant-actions.hook'
import { useChatViewInit } from './use-chat-view-init.hook'

export type { ChatViewHandlers, ChatViewState, ChatViewTitleEditState }

export interface WebSocketState {
  connectionStatus: WebSocketConnectionStatus
  onReconnect: () => void
}

export interface UseChatViewReturn {
  state: ChatViewState
  handlers: ChatViewHandlers
  titleEditState: ChatViewTitleEditState
  splitView: ReturnType<typeof useSplitView>
  participantActions: UseChatParticipantActionsReturn
  websocketState: WebSocketState
}

export const useChatView = (): UseChatViewReturn => {
  const init = useChatViewInit()

  return {
    state: buildChatViewState({
      messages: init.messages,
      participantList: init.participantList,
      pinnedMessageList: init.pinnedMessageList,
      highlightedMessageId: init.highlightedMessageId,
      jumpingPinnedMessageId: init.jumpingPinnedMessageId,
      virtualListBinding: init.virtualListBinding,
      input: init.input,
      conversationId: init.conversationState.conversationId,
      conversation: init.conversationState.conversation,
      isNewConversation: init.conversationState.isNewConversation,
      currentUserId: init.user?.id ?? '',
      isBalanceDepleted: init.isBalanceDepleted,
      isPlanRestricted: init.isPlanRestricted,
      userInteractionCardList: init.userInteractionCardList,
      isStreaming: init.isStreaming,
      isStopping: init.isStopping,
      isUploading: init.isUploading,
      uploadProgress: init.uploadProgress,
      agencySlug: init.conversationState.agencySlug,
    }),
    handlers: buildChatViewHandlers({
      input: init.input,
      splitViewActions: init.splitViewActions,
      headerActions: init.headerActions,
      handleSend: init.handleSend,
      handleStop: init.handleStop,
      handleAttachmentDownload: init.handleAttachmentDownload,
    }),
    titleEditState: buildTitleEditState(init.headerActions),
    splitView: init.splitViewActions.splitView,
    participantActions: init.participantActions,
    websocketState: {
      connectionStatus: init.websocket.connectionStatus,
      onReconnect: init.websocket.initializeConnection,
    },
  }
}
